import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";

import { SERVER_URL } from "@/config/config";
import {
  claimWeeklyMission,
  getUserMissionStatus,
  startWeeklyMission,
} from "@/helper/contractFunctions/MafiaWeeklyMission";
import type { CommonMissionStatus } from "@/types/WeeklyMission/CommonMissionStatus";
import type { SmuggleMission } from "@/types/WeeklyMission/SmuggleMission";
import type { WeeklyLeaderboardEntry } from "@/types/WeeklyMission/WeeklyLeaderboardEntry";
import type { WeeklyLeaderboardTotalRewardEntry } from "@/types/WeeklyMission/WeeklyLeaderboardTotalRewardEntry";

export interface WeeklyMissionStatus {
  startedAt: number;
  completedAt: number;
  status: number;
  maxReward: number;
  commonMissions: CommonMissionStatus[];
  smuggleMissions: SmuggleMission[];
}

export interface WeeklyMissionsState {
  isStartingWeeklyMission: boolean;
  isClaimingWeeklyMission: boolean;
  isLoadingWeeklyMissionStatus: boolean;
  weeklyMissionStatus: WeeklyMissionStatus;
  weeklyMissionProgress: number;
  isLoadingWeeklyLeaderboard: boolean;
  weeklyLeaderboard: WeeklyLeaderboardEntry[];
  isLoadingWeeklyLeaderboardTotalReward: boolean;
  weeklyLeaderboardTotalReward: WeeklyLeaderboardTotalRewardEntry[];
}

const initialState: WeeklyMissionsState = {
  isStartingWeeklyMission: false,
  isClaimingWeeklyMission: false,
  isLoadingWeeklyMissionStatus: false,
  weeklyMissionStatus: {
    startedAt: 0,
    completedAt: 0,
    status: 0,
    maxReward: 0,
    commonMissions: [],
    smuggleMissions: [],
  },
  weeklyMissionProgress: 0,
  isLoadingWeeklyLeaderboard: false,
  weeklyLeaderboard: [],
  isLoadingWeeklyLeaderboardTotalReward: false,
  weeklyLeaderboardTotalReward: [],
};

export const getUserMissionStatusAction = createAsyncThunk(
  "weeklyMissions/getUserMissionStatusAction",
  getUserMissionStatus
);

export const startWeeklyMissionAction = createAsyncThunk(
  "weeklyMissions/startWeeklyMissionAction",
  async (account: string) => (await startWeeklyMission(account)).transactionHash
);

export const claimWeeklyMissionAction = createAsyncThunk(
  "weeklyMissions/claimWeeklyMissionAction",
  async (account: string) => (await claimWeeklyMission(account)).transactionHash
);

export const getWeeklyLeaderboardAction = createAsyncThunk(
  "weeklyMissions/getWeeklyLeaderboardAction",
  async (sortBy: "profit" | "time" | "totalReward") =>
    (await axios.get(`${SERVER_URL}/weekly-mission/claims/${sortBy}`)).data
);

export const getWeeklyLeaderboardTotalRewardAction = createAsyncThunk(
  "weeklyMissions/getWeeklyLeaderboardTotalRewardAction",
  async () =>
    (await axios.get(`${SERVER_URL}/weekly-mission/claims/totalReward`)).data
);

const weeklyMissionsSlice = createSlice({
  name: "weeklyMissions",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getUserMissionStatusAction.pending, (state) => {
        state.isLoadingWeeklyMissionStatus = true;
      })
      .addCase(getUserMissionStatusAction.fulfilled, (state, { payload }) => {
        state.isLoadingWeeklyMissionStatus = false;
        state.weeklyMissionStatus = payload;
        const completedCount =
          payload.commonMissions.filter((mission: CommonMissionStatus) => mission.isCompleted).length +
          payload.smuggleMissions.filter((mission: SmuggleMission) => mission.isCompleted).length;
        const missionCount =
          payload.commonMissions.length + payload.smuggleMissions.length;
        state.weeklyMissionProgress =
          Math.round((completedCount * 100) / missionCount) || 0;
      })
      .addCase(getUserMissionStatusAction.rejected, (state, { error }) => {
        state.isLoadingWeeklyMissionStatus = false;
        console.log(error.message);
      })
      .addCase(startWeeklyMissionAction.pending, (state) => {
        state.isStartingWeeklyMission = true;
      })
      .addCase(startWeeklyMissionAction.fulfilled, (state) => {
        state.isStartingWeeklyMission = false;
      })
      .addCase(startWeeklyMissionAction.rejected, (state, { error }) => {
        state.isStartingWeeklyMission = false;
        console.log(error.message);
      })
      .addCase(claimWeeklyMissionAction.pending, (state) => {
        state.isClaimingWeeklyMission = true;
      })
      .addCase(claimWeeklyMissionAction.fulfilled, (state) => {
        state.isClaimingWeeklyMission = false;
      })
      .addCase(claimWeeklyMissionAction.rejected, (state, { error }) => {
        state.isClaimingWeeklyMission = false;
        console.log(error.message);
      })
      .addCase(getWeeklyLeaderboardAction.pending, (state) => {
        state.isLoadingWeeklyLeaderboard = true;
      })
      .addCase(getWeeklyLeaderboardAction.fulfilled, (state, { payload }) => {
        state.isLoadingWeeklyLeaderboard = false;
        state.weeklyLeaderboard = payload.data;
      })
      .addCase(getWeeklyLeaderboardAction.rejected, (state, { error }) => {
        state.isLoadingWeeklyLeaderboard = false;
        console.log(error.message);
      })
      .addCase(getWeeklyLeaderboardTotalRewardAction.pending, (state) => {
        state.isLoadingWeeklyLeaderboardTotalReward = true;
      })
      .addCase(
        getWeeklyLeaderboardTotalRewardAction.fulfilled,
        (state, { payload }) => {
          state.isLoadingWeeklyLeaderboardTotalReward = false;
          state.weeklyLeaderboardTotalReward = payload.data;
        }
      )
      .addCase(
        getWeeklyLeaderboardTotalRewardAction.rejected,
        (state, { error }) => {
          state.isLoadingWeeklyLeaderboardTotalReward = false;
          console.log(error.message);
        }
      );
  },
});

export default weeklyMissionsSlice.reducer;
